// src/pages/relatorios/RelatorioPedidosAprovados.jsx
import React, { useState, useEffect } from 'react';
import { 
  FaFilter, FaFileExcel, FaSearch, FaCalendarAlt,
  FaIdCard, FaUsers, FaBuilding, FaTools,
  FaSpinner, FaInfoCircle, FaCheckCircle, FaDollarSign, FaPercentage
} from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import supabase from '../../supabaseClient';
import * as XLSX from 'xlsx';

const RelatorioPedidosAprovados = () => {
  const { hasRole, user, userProfile } = useAuth();
  
  // Estados
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [expositorLogado, setExpositorLogado] = useState(null);
  const [expositores, setExpositores] = useState([]);
  const [eventos, setEventos] = useState([]);
  
  // Estados para filtros
  const [filters, setFilters] = useState({
    dataInicio: '',
    dataFim: '',
    expositor: '',
    nomeExpositor: '',
    evento: '',
    valorMinimo: '',
    valorMaximo: '',
    tipoDesconto: ''
  });
  
  // Estados para estatísticas
  const [stats, setStats] = useState({
    totalPedidos: 0,
    valorTotalPedidos: 0,
    valorMedioPedido: 0,
    totalDesconto: 0,
    totalExpositores: 0,
    totalEventos: 0
  });

  // Buscar expositor logado baseado no email
  const fetchExpositorLogado = async () => {
    if (!user?.email || hasRole('admin')) {
      return null; // Admin vê todos os pedidos
    }

    try {
      const { data, error } = await supabase
        .from('caex_expositores')
        .select('*')
        .eq('email', user.email)
        .eq('ativo', true)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Erro ao buscar expositor logado:', error);
      return null;
    }
  };

  // Carregar expositores para o filtro (apenas para admin)
  const fetchExpositores = async () => {
    if (!hasRole('admin')) return;

    try {
      const { data, error } = await supabase
        .from('caex_expositores')
        .select('id, nome_empresa, cnpj, email')
        .eq('ativo', true)
        .order('nome_empresa');
      
      if (error) throw error;
      setExpositores(data || []);
    } catch (error) {
      console.error('Erro ao buscar expositores:', error);
    }
  };

  // Carregar eventos para o filtro
  const fetchEventos = async () => {
    try {
      const { data, error } = await supabase
        .from('caex_eventos')
        .select('id, nome, data_inicio, data_fim')
        .order('nome');
      
      if (error) throw error;
      setEventos(data || []);
    } catch (error) {
      console.error('Erro ao buscar eventos:', error);
    }
  };

  // Carregar dados dos pedidos aprovados com filtros aplicados
  const fetchPedidos = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Buscando pedidos aprovados com filtros:', filters);
      
      // Primeiro buscar expositor se for expositor logado
      const expositor = await fetchExpositorLogado();
      setExpositorLogado(expositor);
      
      // Query base para buscar pedidos aprovados
      let query = supabase
        .from('caex_pedidos')
        .select(`
          id,
          data_pedido,
          created_at,
          valor_total,
          valor_original,
          desconto,
          tipo_desconto,
          status,
          observacoes,
          expositor_id,
          evento_id,
          caex_expositores (
            id,
            nome_empresa,
            cnpj,
            email
          ),
          caex_eventos (
            id,
            nome,
            data_inicio,
            data_fim
          )
        `)
        .eq('status', 'aprovado') // Apenas pedidos aprovados
        .order('data_pedido', { ascending: false });

      // Aplicar filtro por expositor se não for admin
      if (!hasRole('admin') && expositor) {
        query = query.eq('expositor_id', expositor.id);
      }

      // Aplicar filtros de data
      if (filters.dataInicio) {
        query = query.gte('data_pedido', `${filters.dataInicio}T00:00:00`);
      }

      if (filters.dataFim) {
        query = query.lte('data_pedido', `${filters.dataFim}T23:59:59`);
      }
      
      // Filtro por ID do expositor (para admin)
      if (filters.expositor && hasRole('admin')) {
        query = query.eq('expositor_id', filters.expositor);
      }

      // Filtro por evento
      if (filters.evento) {
        query = query.eq('evento_id', filters.evento);
      }

      // Filtro por tipo de desconto
      if (filters.tipoDesconto) {
        query = query.eq('tipo_desconto', filters.tipoDesconto);
      }

      // Filtros de valor (aplicados após a query)
      const { data, error: fetchError } = await query;

      if (fetchError) {
        console.error('Erro ao buscar pedidos:', fetchError);
        throw fetchError;
      }

      console.log('Pedidos carregados:', data);

      // Verificar se o expositor foi encontrado (apenas para não-admin)
      if (!hasRole('admin') && !expositor) {
        setError('Expositor não encontrado para este usuário. Entre em contato com o administrador.');
        setLoading(false);
        return;
      }

      // Processar dados para o relatório
      let pedidosProcessados = [];
      const expositoresUnicos = new Set();
      const eventosUnicos = new Set();
      let valorTotalSoma = 0;
      let totalDescontoSoma = 0;

      data.forEach((pedido) => {
        const dataPedido = new Date(pedido.data_pedido).toLocaleDateString('pt-BR');
        const horaPedido = new Date(pedido.data_pedido).toLocaleTimeString('pt-BR');
        const nomeExpositor = pedido.caex_expositores?.nome_empresa || 'Expositor não encontrado';
        const nomeEvento = pedido.caex_eventos?.nome || 'Evento não informado';
        const valorOriginal = parseFloat(pedido.valor_original || 0);
        const desconto = parseFloat(pedido.desconto || 0);
        const valorTotal = parseFloat(pedido.valor_total || 0);
        const tipoDesconto = pedido.tipo_desconto || 'value';
        
        // Calcular percentual de desconto
        let percentualDesconto = 0;
        if (valorOriginal > 0) {
          if (tipoDesconto === 'percentage') {
            percentualDesconto = desconto;
          } else {
            percentualDesconto = ((desconto / valorOriginal) * 100);
          }
        }

        pedidosProcessados.push({
          pedidoId: pedido.id,
          dataPedido,
          horaPedido,
          nomeExpositor,
          nomeEvento,
          valorOriginal,
          desconto,
          tipoDesconto,
          percentualDesconto: percentualDesconto.toFixed(2),
          valorTotal,
          status: pedido.status,
          observacoes: pedido.observacoes || '',
          expositorId: pedido.expositor_id,
          eventoId: pedido.evento_id
        });

        // Para estatísticas
        if (pedido.expositor_id) expositoresUnicos.add(pedido.expositor_id);
        if (pedido.evento_id) eventosUnicos.add(pedido.evento_id);
        valorTotalSoma += valorTotal;
        totalDescontoSoma += desconto;
      });

      // Aplicar filtros locais que não podem ser aplicados na query SQL
      if (filters.nomeExpositor) {
        pedidosProcessados = pedidosProcessados.filter(pedido =>
          pedido.nomeExpositor.toLowerCase().includes(filters.nomeExpositor.toLowerCase())
        );
      }

      if (filters.valorMinimo) {
        pedidosProcessados = pedidosProcessados.filter(pedido =>
          pedido.valorTotal >= parseFloat(filters.valorMinimo)
        );
      }

      if (filters.valorMaximo) {
        pedidosProcessados = pedidosProcessados.filter(pedido =>
          pedido.valorTotal <= parseFloat(filters.valorMaximo)
        );
      }

      // Recalcular estatísticas após filtros locais
      const valorTotalFiltrado = pedidosProcessados.reduce((sum, p) => sum + p.valorTotal, 0);
      const totalDescontoFiltrado = pedidosProcessados.reduce((sum, p) => sum + p.desconto, 0);

      setPedidos(pedidosProcessados);
      setStats({
        totalPedidos: pedidosProcessados.length,
        valorTotalPedidos: valorTotalFiltrado,
        valorMedioPedido: pedidosProcessados.length > 0 ? valorTotalFiltrado / pedidosProcessados.length : 0,
        totalDesconto: totalDescontoFiltrado,
        totalExpositores: expositoresUnicos.size,
        totalEventos: eventosUnicos.size
      });

    } catch (err) {
      console.error('Erro ao carregar pedidos:', err);
      setError(err.message || 'Erro ao carregar dados dos pedidos aprovados');
    } finally {
      setLoading(false);
    }
  };

  // Função para exportar para Excel
  const exportToExcel = () => {
    try {
      if (pedidos.length === 0) {
        alert('Não há dados para exportar.');
        return;
      }

      // Preparar dados para o Excel baseado no perfil do usuário
      const dadosExcel = pedidos.map(pedido => {
        if (hasRole('admin')) {
          // Para administradores: todos os campos
          return {
            'Nº': pedido.pedidoId,
            'Data': pedido.dataPedido,
            'Hora': pedido.horaPedido,
            'Expositor': pedido.nomeExpositor,
            'Evento': pedido.nomeEvento,
            'Valor Final': `R$ ${pedido.valorTotal.toFixed(2)}`,
            'Status': pedido.status,
            'Observações': pedido.observacoes
          };
        } else {
          // Para expositores: sem expositor
          return {
            'Nº': pedido.pedidoId,
            'Data': pedido.dataPedido,
            'Hora': pedido.horaPedido,
            'Evento': pedido.nomeEvento,
            'Valor Final': `R$ ${pedido.valorTotal.toFixed(2)}`,
            'Status': pedido.status,
            'Observações': pedido.observacoes
          };
        }
      });

      // Criar workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(dadosExcel);

      // Configurar larguras das colunas
      const colWidths = hasRole('admin') ? [
        { wch: 8 },  // Nº
        { wch: 12 }, // Data
        { wch: 10 }, // Hora
        { wch: 30 }, // Expositor
        { wch: 25 }, // Evento
        { wch: 15 }, // Valor Final
        { wch: 10 }, // Status
        { wch: 30 }  // Observações
      ] : [
        { wch: 8 },  // Nº
        { wch: 12 }, // Data
        { wch: 10 }, // Hora
        { wch: 25 }, // Evento
        { wch: 15 }, // Valor Final
        { wch: 10 }, // Status
        { wch: 30 }  // Observações
      ];
      
      worksheet['!cols'] = colWidths;

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Pedidos Aprovados');

      // Gerar nome do arquivo com data/hora atual
      const now = new Date();
      const dataHora = now.toISOString().slice(0, 19).replace(/:/g, '-');
      const nomeArquivo = `pedidos_aprovados_${dataHora}.xlsx`;

      // Salvar arquivo
      XLSX.writeFile(workbook, nomeArquivo);

      console.log('Excel exportado com sucesso:', nomeArquivo);
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      alert('Erro ao exportar para Excel. Tente novamente.');
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    if (user?.email) {
      fetchExpositores();
      fetchEventos();
      fetchPedidos();
    }
  }, [user]);

  // Aplicar filtros
  const handleApplyFilters = () => {
    fetchPedidos();
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilters({
      dataInicio: '',
      dataFim: '',
      expositor: '',
      nomeExpositor: '',
      evento: '',
      valorMinimo: '',
      valorMaximo: '',
      tipoDesconto: ''
    });
    setTimeout(() => {
      fetchPedidos();
    }, 100);
  };

  // Handler para mudanças nos filtros
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Formatação de valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Renderizar estatísticas
  const renderStats = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '30px' }}>
      {/* Primeira linha - 4 cards principais */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px' 
      }}>
        <div className="stat-card">
          <div className="stat-icon">
            <FaCheckCircle />
          </div>
          <div className="stat-content">
            <h3>{stats.totalPedidos}</h3>
            <p>Pedidos Aprovados</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FaDollarSign />
          </div>
          <div className="stat-content">
            <h3>{formatCurrency(stats.valorTotalPedidos)}</h3>
            <p>Valor Total</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FaPercentage />
          </div>
          <div className="stat-content">
            <h3>{formatCurrency(stats.valorMedioPedido)}</h3>
            <p>Valor Médio</p>
          </div>
        </div>
      </div>
      
      {/* Segunda linha - 2 cards secundários centralizados */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '20px',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        <div className="stat-card">
          <div className="stat-icon">
            <FaUsers />
          </div>
          <div className="stat-content">
            <h3>{stats.totalExpositores}</h3>
            <p>Expositores</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FaCalendarAlt />
          </div>
          <div className="stat-content">
            <h3>{stats.totalEventos}</h3>
            <p>Eventos</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Renderizar filtros
  const renderFilters = () => (
    <div className="filters-section">
      <div className="filters-grid">
        {/* Primeira linha - Datas */}
        <div className="filter-group">
          <label>Data Início</label>
          <input
            type="date"
            value={filters.dataInicio}
            onChange={(e) => handleFilterChange('dataInicio', e.target.value)}
            className="filter-input"
          />
        </div>
        
        <div className="filter-group">
          <label>Data Fim</label>
          <input
            type="date"
            value={filters.dataFim}
            onChange={(e) => handleFilterChange('dataFim', e.target.value)}
            className="filter-input"
          />
        </div>

        {/* Expositor (apenas para admin) */}
        {hasRole('admin') && (
          <div className="filter-group">
            <label>Expositor</label>
            <select
              value={filters.expositor}
              onChange={(e) => handleFilterChange('expositor', e.target.value)}
              className="filter-input"
            >
              <option value="">Todos os Expositores</option>
              {expositores.map(expositor => (
                <option key={expositor.id} value={expositor.id}>
                  {expositor.nome_empresa}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-group">
          <label>Nome do Expositor</label>
          <input
            type="text"
            placeholder="Digite o nome do expositor..."
            value={filters.nomeExpositor}
            onChange={(e) => handleFilterChange('nomeExpositor', e.target.value)}
            className="filter-input"
          />
        </div>

        {/* Segunda linha */}
        <div className="filter-group">
          <label>Evento</label>
          <select
            value={filters.evento}
            onChange={(e) => handleFilterChange('evento', e.target.value)}
            className="filter-input"
          >
            <option value="">Todos os Eventos</option>
            {eventos.map(evento => (
              <option key={evento.id} value={evento.id}>
                {evento.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Valor Mínimo</label>
          <input
            type="number"
            step="0.01"
            placeholder="0,00"
            value={filters.valorMinimo}
            onChange={(e) => handleFilterChange('valorMinimo', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Valor Máximo</label>
          <input
            type="number"
            step="0.01"
            placeholder="0,00"
            value={filters.valorMaximo}
            onChange={(e) => handleFilterChange('valorMaximo', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Tipo de Desconto</label>
          <select
            value={filters.tipoDesconto}
            onChange={(e) => handleFilterChange('tipoDesconto', e.target.value)}
            className="filter-input"
          >
            <option value="">Todos os Tipos</option>
            <option value="value">Valor (R$)</option>
            <option value="percentage">Percentual (%)</option>
          </select>
        </div>
      </div>
      
      <div className="filters-actions">
        <button 
          className="btn-filter apply"
          onClick={handleApplyFilters}
          disabled={loading}
        >
          <FaSearch />
          Aplicar Filtros
        </button>
        
        <button 
          className="btn-filter clear"
          onClick={handleClearFilters}
          disabled={loading}
        >
          Limpar Filtros
        </button>
      </div>
    </div>
  );

  // Renderizar tabela
  const renderTable = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <FaSpinner className="loading-spinner" />
          <p>Carregando dados dos pedidos...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-container">
          <FaInfoCircle className="error-icon" />
          <h3>Erro ao carregar dados</h3>
          <p>{error}</p>
          {error.includes('Expositor não encontrado') ? (
            <div className="error-details">
              <p><strong>Email do usuário:</strong> {user?.email}</p>
              <p>Verifique se existe um expositor cadastrado com este email e se está ativo no sistema.</p>
            </div>
          ) : (
            <button className="btn-retry" onClick={fetchPedidos}>
              Tentar Novamente
            </button>
          )}
        </div>
      );
    }

    if (pedidos.length === 0) {
      return (
        <div className="empty-container">
          <FaCheckCircle className="empty-icon" />
          <h3>Nenhum pedido aprovado encontrado</h3>
          <p>Não há pedidos aprovados que correspondam aos filtros aplicados.</p>
        </div>
      );
    }

    // Colunas condicionais baseadas no perfil do usuário
    const renderTableHeaders = () => {
      if (hasRole('admin')) {
        return (
          <tr>
            <th>Nº</th>
            <th>Data/Hora</th>
            <th>Expositor</th>
            <th>Evento</th>
            <th>Valor Final</th>
            <th>Status</th>
          </tr>
        );
      } else {
        return (
          <tr>
            <th>Nº</th>
            <th>Data/Hora</th>
            <th>Evento</th>
            <th>Valor Final</th>
            <th>Status</th>
          </tr>
        );
      }
    };

    const renderTableRows = () => {
      return pedidos.map((pedido) => {
        if (hasRole('admin')) {
          return (
            <tr key={pedido.pedidoId}>
              <td>{pedido.pedidoId}</td>
              <td>
                <div>{pedido.dataPedido}</div>
                <small style={{color: '#666'}}>{pedido.horaPedido}</small>
              </td>
              <td>{pedido.nomeExpositor}</td>
              <td>{pedido.nomeEvento}</td>
              <td><strong>{formatCurrency(pedido.valorTotal)}</strong></td>
              <td>
                <span className="status-badge approved" style={{fontSize: '16px', fontWeight: '600'}}>{pedido.status}</span>
              </td>
            </tr>
          );
        } else {
          return (
            <tr key={pedido.pedidoId}>
              <td>{pedido.pedidoId}</td>
              <td>
                <div>{pedido.dataPedido}</div>
                <small style={{color: '#666'}}>{pedido.horaPedido}</small>
              </td>
              <td>{pedido.nomeEvento}</td>
              <td><strong>{formatCurrency(pedido.valorTotal)}</strong></td>
              <td>
                <span className="status-badge approved" style={{fontSize: '16px', fontWeight: '600'}}>{pedido.status}</span>
              </td>
            </tr>
          );
        }
      });
    };

    return (
      <div className="table-container">
        <table className="report-table">
          <thead>
            {renderTableHeaders()}
          </thead>
          <tbody>
            {renderTableRows()}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="relatorio-container">
      {/* Header com estatísticas */}
      <div className="relatorio-header">
        {/* Informação do expositor se não for admin */}
        {!hasRole('admin') && expositorLogado && (
          <div className="expositor-info">
            <h4>Relatório de: {expositorLogado.nome_empresa}</h4>
            <p>CNPJ: {expositorLogado.cnpj}</p>
          </div>
        )}
        
        <div className="header-controls">
          <button 
            className={`btn-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </button>
          
          <div className="export-buttons">
            <button 
              className="btn-export excel" 
              onClick={exportToExcel}
              disabled={loading || pedidos.length === 0}
            >
              <FaFileExcel />
              Excel
            </button>
          </div>
        </div>

        {renderStats()}
      </div>

      {/* Filtros */}
      {showFilters && renderFilters()}

      {/* Tabela de dados */}
      <div className="relatorio-content">
        {renderTable()}
      </div>
    </div>
  );
};

export default RelatorioPedidosAprovados;